import { Injectable, computed, signal } from '@angular/core';

import {
  PostDetailsScope,
  injectPostDetailsQuery,
} from 'src/app/api/queries/post.queries';

import { Post } from '../models/core.models';

/**
 * The post details pane's post. One post is open at a time: the pane lives in
 * `feed.component`, its two variants are mutually exclusive and its seven tab panels
 * are created through a `ViewContainerRef`. So the query is mounted **once**, here,
 * and the panels read it through `postFor` / `isLoadingFor` - they are handed a
 * `postId` and nothing else, and a root service is the only way to reach the same
 * cache entry without threading `feedId` through seven imperative inputs.
 *
 * Every write is a `patchPostDetails` call in the mutation hook that made it; this
 * service never writes.
 */
@Injectable({ providedIn: 'root' })
export class PostDetailsService {
  private readonly scope = signal<PostDetailsScope | null>(null);

  private readonly query = injectPostDetailsQuery(this.scope);

  private readonly post = computed(() => this.query.data() ?? null);

  /**
   * A repeat call for the post already open **refetches**: a root-held query has no
   * per-mount refetch to fall back on, and re-opening the pane has to re-read.
   */
  open(postId: string | null, feedId: string): void {
    const current = this.scope();

    if (!postId) {
      this.scope.set(null);
      return;
    }

    if (current?.postId === postId && current?.feedId === feedId) {
      void this.query.refetch();
      return;
    }

    this.scope.set({ postId, feedId });
  }

  /**
   * The open post, or null for any other id - the panels keep their own
   * `postId`, and one that has fallen behind the pane must render nothing rather
   * than the wrong post's content.
   */
  postFor(postId: string | null): Post | null {
    if (!postId || this.scope()?.postId !== postId) return null;

    return this.post();
  }

  isLoadingFor(postId: string | null): boolean {
    if (!postId || this.scope()?.postId !== postId) return false;

    return this.query.isLoading();
  }
}
